import {FC, useEffect, useState, useCallback} from 'react'
import './issues.css'
import IssuesCalendar from './IssuesCalendar'
import {
  getIssues,
  createIssue,
  updateIssue,
  deleteIssue,
  addIssueAccounts,
  updateIssueAccount,
  removeIssueAccount,
  replyIssue,
  confirmIssueFixed,
  unconfirmIssueFixed,
  generateIssueFixPrompt,
  confirmIssueAccountFixed,
  deleteIssueReply,
  searchAccounts,
  uploadIssueMedia,
  switchToAccount,
  getIssueAdmins,
} from './core/_requests'
import {
  AccountIssue,
  IssueStatus,
  AccountSearchResult,
  IssueAttachment,
  WaitingState,
  IssueUrgency,
  AdminOption,
  ISSUE_DEPARTMENTS,
} from './core/_models'
import {useAdminAuth} from '../auth/AdminAuthContext'
import {
  StatusPill,
  WaitingPill,
  Avatar,
  AttachmentList,
  MediaDropzone,
  Linkify,
  timeAgo,
  fullDate,
  STATUS_META,
  STATUS_ORDER,
  URGENCY_META,
  URGENCY_ORDER,
  UrgencySelect,
} from './core/ui'

async function loginAsAccount(accountId: string) {
  try {
    const res = await switchToAccount(accountId)
    if (res.data.success && res.data.token) {
      window.location.href = '/auth/auto-login?token=' + encodeURIComponent(res.data.token)
    } else {
      alert('לא ניתן להתחבר לחשבון')
    }
  } catch {
    alert('שגיאה בהתחברות לחשבון')
  }
}

// Compact 3-way status segmented control.
const StatusSeg: FC<{value: IssueStatus; onChange: (s: IssueStatus) => void}> = ({value, onChange}) => (
  <div className='iss-seg'>
    {STATUS_ORDER.map((s) => (
      <button key={s} className={value === s ? 'active' : ''} onClick={() => onChange(s)}>
        {STATUS_META[s].label}
      </button>
    ))}
  </div>
)

// Inline account search box used for "new issue" and "add accounts to issue".
const AccountPicker: FC<{
  selected: AccountSearchResult[]
  onChange: (accts: AccountSearchResult[]) => void
}> = ({selected, onChange}) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<AccountSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (q.trim().length < 1) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const res = await searchAccounts(q.trim())
        if (!cancelled) setResults(res.data.accounts || [])
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [q])

  const add = (a: AccountSearchResult) => {
    if (selected.some((s) => s._id === a._id)) return
    onChange([...selected, a])
    setQ('')
    setResults([])
  }
  const remove = (id: string) => onChange(selected.filter((s) => s._id !== id))

  return (
    <div>
      {selected.length > 0 && (
        <div className='d-flex flex-wrap gap-2 mb-2'>
          {selected.map((a) => (
            <span
              key={a._id}
              className='iss-chip'
              style={{background: 'rgba(91,110,245,.1)', color: '#5b6ef5'}}
            >
              {(a.first_name || '') + ' ' + (a.last_name || '')}
              <i className='bi bi-x' style={{cursor: 'pointer'}} onClick={() => remove(a._id)}></i>
            </span>
          ))}
        </div>
      )}
      <input
        className='iss-input'
        placeholder='חיפוש חשבון לפי שם / אימייל...'
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {(results.length > 0 || searching) && (
        <div className='iss-results'>
          {searching && <div className='iss-result-item text-muted'>מחפש...</div>}
          {results.map((a) => (
            <div key={a._id} className='iss-result-item' onClick={() => add(a)}>
              <div className='fw-bold' style={{fontSize: 13}}>
                {(a.first_name || '') + ' ' + (a.last_name || '')}
              </div>
              <div style={{fontSize: 11, color: '#8a92a6'}}>{a.email}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const WAITING_SEG: {value: WaitingState; label: string}[] = [
  {value: 'none', label: 'ללא'},
  {value: 'aryeh', label: 'אריה (פיתוח)'},
  {value: 'support', label: 'תמיכה'},
]

const IssueCard: FC<{
  issue: AccountIssue
  admins: AdminOption[]
  onChanged: (issue: AccountIssue) => void
  onDeleted: (id: string) => void
}> = ({issue, admins, onChanged, onDeleted}) => {
  const {admin} = useAdminAuth()
  const [expanded, setExpanded] = useState(false)
  const [adding, setAdding] = useState<AccountSearchResult[]>([])
  const [showAddAccts, setShowAddAccts] = useState(false)
  const [reply, setReply] = useState('')
  const [replyFiles, setReplyFiles] = useState<IssueAttachment[]>([])
  const [replyUploading, setReplyUploading] = useState(false)

  const openOnAccount = issue.accounts.filter((a) => a.status !== 'done').length

  const setOverallStatus = async (status: IssueStatus) => onChanged((await updateIssue(issue._id, {status})).data.issue)
  const [confirming, setConfirming] = useState(false)
  const confirmFixed = async (e?: any) => {
    if (e) e.stopPropagation()
    if (!window.confirm('לאשר שהתקלה תוקנה?')) return
    setConfirming(true)
    try { onChanged((await confirmIssueFixed(issue._id)).data.issue) } finally { setConfirming(false) }
  }
  const unconfirmFixed = async (e?: any) => {
    if (e) e.stopPropagation()
    if (!window.confirm('לבטל את אישור התיקון?')) return
    setConfirming(true)
    try { onChanged((await unconfirmIssueFixed(issue._id)).data.issue) } finally { setConfirming(false) }
  }
  // AI: generate a ready-to-paste Claude Code fix prompt from the issue.
  const [aiOpen, setAiOpen] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiResult, setAiResult] = useState<{relevant: boolean; prompt: string; reason: string} | null>(null)
  const [aiCopied, setAiCopied] = useState(false)
  const genFixPrompt = async (e?: any) => {
    if (e) e.stopPropagation()
    setAiOpen(true)
    setAiLoading(true)
    setAiResult(null)
    setAiCopied(false)
    try {
      const res = await generateIssueFixPrompt(issue._id)
      setAiResult(res.data)
    } catch (err: any) {
      setAiResult({relevant: false, prompt: '', reason: err?.response?.data?.message || 'שגיאה ביצירת הפרומפט'})
    } finally {
      setAiLoading(false)
    }
  }
  const copyAiPrompt = async () => {
    if (!aiResult?.prompt) return
    try {
      await navigator.clipboard.writeText(aiResult.prompt)
      setAiCopied(true)
      setTimeout(() => setAiCopied(false), 2000)
    } catch (e) {
      /* clipboard may be blocked; user can still select the text */
    }
  }
  const confirmAccountFixed = async (accountId: string) => {
    if (!window.confirm('לאשר שתוקן עבור חשבון זה?')) return
    onChanged((await confirmIssueAccountFixed(issue._id, accountId)).data.issue)
  }
  // Confirm-fixed badge/button — only relevant once the issue is marked done.
  const renderConfirmFixed = () => {
    if (issue.fixed_confirmed_at) {
      return (
        <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}} onClick={(e) => e.stopPropagation()}>
          <span style={{fontSize: 11, color: '#16a34a', fontWeight: 700, background: '#dcfce7', borderRadius: 8, padding: '4px 10px', whiteSpace: 'nowrap'}} title={fullDate(issue.fixed_confirmed_at)}>
            <i className='bi bi-patch-check-fill me-1'></i>אושר כתוקן{issue.fixed_confirmed_by ? ' · ' + issue.fixed_confirmed_by : ''}
          </span>
          <button className='iss-btn iss-btn-sm' style={{background: '#fff1f4', color: '#b91c1c', fontWeight: 700, whiteSpace: 'nowrap'}} onClick={unconfirmFixed} disabled={confirming} title='בטל את אישור התיקון'>
            {confirming ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='bi bi-arrow-counterclockwise me-1'></i>}
            בטל אישור
          </button>
        </span>
      )
    }
    if (issue.status !== 'done') return null
    return (
      <button className='iss-btn' style={{background: '#dcfce7', color: '#15803d', fontWeight: 700, whiteSpace: 'nowrap'}} onClick={confirmFixed} disabled={confirming} title='התמיכה מאשרת שהתקלה תוקנה'>
        {confirming ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='bi bi-patch-check me-1'></i>}
        אשר שתוקן
      </button>
    )
  }
  const setWaiting = async (waiting: WaitingState) => onChanged((await updateIssue(issue._id, {waiting})).data.issue)
  const setUrgency = async (urgency: IssueUrgency) => onChanged((await updateIssue(issue._id, {urgency})).data.issue)
  const setAssignee = async (assigned_to: string) =>
    onChanged((await updateIssue(issue._id, {assigned_to: assigned_to || null})).data.issue)
  const setIssueDepartment = async (department: string) =>
    // Changing department also clears an assignee who isn't in the new department.
    onChanged((await updateIssue(issue._id, {
      department: department || null,
      ...(department && issue.assigned_to && !admins.find((a) => a._id === issue.assigned_to?._id)?.departments?.includes(department)
        ? {assigned_to: null}
        : {}),
    })).data.issue)
  const setAccountStatus = async (accountId: string, status: IssueStatus) =>
    onChanged((await updateIssueAccount(issue._id, accountId, {status})).data.issue)
  const setAccountNote = async (accountId: string, note: string) =>
    onChanged((await updateIssueAccount(issue._id, accountId, {note})).data.issue)
  const doAddAccounts = async () => {
    if (!adding.length) return
    const res = await addIssueAccounts(issue._id, adding.map((a) => a._id))
    setAdding([])
    setShowAddAccts(false)
    onChanged(res.data.issue)
  }
  const doRemoveAccount = async (accountId: string) =>
    onChanged((await removeIssueAccount(issue._id, accountId)).data.issue)
  const onPickReplyFiles = async (files: FileList) => {
    setReplyUploading(true)
    try {
      const uploaded: IssueAttachment[] = []
      for (let i = 0; i < files.length; i++) uploaded.push((await uploadIssueMedia(files[i])).data)
      setReplyFiles((prev) => [...prev, ...uploaded])
    } finally {
      setReplyUploading(false)
    }
  }
  const doReply = async () => {
    if (!reply.trim() && !replyFiles.length) return
    const res = await replyIssue(issue._id, reply.trim(), replyFiles)
    setReply('')
    setReplyFiles([])
    onChanged(res.data.issue)
  }
  const doDeleteReply = async (replyId: string) => {
    if (!window.confirm('למחוק את התגובה?')) return
    onChanged((await deleteIssueReply(issue._id, replyId)).data.issue)
  }
  const doDelete = async () => {
    if (!window.confirm('למחוק את התקלה?')) return
    await deleteIssue(issue._id)
    onDeleted(issue._id)
  }

  return (
    <div className='iss-card iss-fade-up mb-3'>
      {/* Header row (click to expand) */}
      <div
        className='d-flex align-items-center justify-content-between'
        style={{padding: '16px 20px', cursor: 'pointer', gap: 12}}
        onClick={() => setExpanded(!expanded)}
      >
        <div className='d-flex align-items-center' style={{gap: 12, minWidth: 0}}>
          <StatusPill status={issue.status} />
          <div style={{minWidth: 0}}>
            <div className='fw-bold text-truncate' style={{fontSize: 15, color: '#1b1f2a'}}>
              {issue.title || '(ללא כותרת)'}
            </div>
            <div className='text-truncate' style={{fontSize: 12, color: '#8a92a6'}}>
              <i className='bi bi-person-circle me-1'></i>
              {issue.accounts.length === 0
                ? 'ללא חשבון'
                : issue.accounts.length === 1
                ? issue.accounts[0].name || issue.accounts[0].email || 'חשבון'
                : `${issue.accounts[0].name || 'חשבון'} +${issue.accounts.length - 1}`}
            </div>
          </div>
          <WaitingPill waiting={issue.waiting} />
          {issue.due_date && (
            <span className='iss-chip' style={{background: '#eef2ff', color: '#4f46e5'}} title='תאריך ביומן'>
              <i className='bi bi-calendar3'></i>
              {issue.due_date.split('-').reverse().slice(0, 2).join('/')}
            </span>
          )}
        </div>
        <div className='d-flex align-items-center flex-shrink-0' style={{gap: 14}}>
          {/* Urgency — settable directly from the collapsed header */}
          <UrgencySelect value={issue.urgency} onChange={setUrgency} />
          <span style={{fontSize: 12, color: '#8a92a6'}}>
            <i className='bi bi-people me-1'></i>
            {issue.accounts.length} · {openOnAccount} פתוחים
          </span>
          <span style={{fontSize: 12, color: '#8a92a6'}} title={fullDate(issue.created_at)}>
            <i className='bi bi-clock me-1'></i>
            {timeAgo(issue.created_at)}
          </span>
          {renderConfirmFixed()}
          {issue.assigned_to && <Avatar name={issue.assigned_to.username} size={26} />}
          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} style={{color: '#b3b9c7'}}></i>
        </div>
      </div>

      {expanded && (
        <div style={{padding: '4px 20px 20px', borderTop: '1px solid #f1f2f5'}}>
          {/* Controls row */}
          <div className='d-flex flex-wrap align-items-center gap-3 py-3' style={{marginBottom: 4}}>
            <div className='d-flex align-items-center gap-2'>
              <span style={{fontSize: 12, color: '#8a92a6'}}>סטטוס</span>
              <StatusSeg value={issue.status} onChange={setOverallStatus} />
            </div>
            {issue.completed_at && (
              <span style={{fontSize: 11, color: '#16a34a', fontWeight: 600}} title='תאריך סימון כהושלם'>
                <i className='bi bi-check-circle me-1'></i>הושלם · {fullDate(issue.completed_at)}
              </span>
            )}
            {/* Support: confirm the fix actually works (only once done) */}
            {renderConfirmFixed()}
            <div className='d-flex align-items-center gap-2'>
              <span style={{fontSize: 12, color: '#8a92a6'}}>דחיפות</span>
              <div className='iss-seg'>
                {URGENCY_ORDER.map((u) => (
                  <button
                    key={u}
                    className={issue.urgency === u ? 'active' : ''}
                    onClick={() => setUrgency(u)}
                  >
                    {URGENCY_META[u].label}
                  </button>
                ))}
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <span style={{fontSize: 12, color: '#8a92a6'}}>ממתין ל</span>
              <div className='iss-seg'>
                {WAITING_SEG.map((w) => (
                  <button
                    key={w.value}
                    className={issue.waiting === w.value ? 'active' : ''}
                    onClick={() => setWaiting(w.value)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <span style={{fontSize: 12, color: '#8a92a6'}}>מחלקה</span>
              <select
                className='iss-input'
                style={{width: 'auto', padding: '7px 10px'}}
                value={issue.department || ''}
                onChange={(e) => setIssueDepartment(e.target.value)}
                title='שנה מחלקה'
              >
                <option value=''>ללא מחלקה</option>
                {ISSUE_DEPARTMENTS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span style={{fontSize: 12, color: '#8a92a6'}}>משויך ל</span>
              <select
                className='iss-input'
                style={{width: 'auto', padding: '7px 10px'}}
                value={issue.assigned_to?._id || ''}
                onChange={(e) => setAssignee(e.target.value)}
                title='שנה נציג מטפל'
              >
                <option value=''>לא משויך</option>
                {(() => {
                  const list = issue.department ? admins.filter((a) => a.departments?.includes(issue.department!)) : admins
                  const cur = issue.assigned_to
                  const withCur = cur && !list.find((a) => a._id === cur._id) ? [cur, ...list] : list
                  return withCur.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.username}
                    </option>
                  ))
                })()}
              </select>
            </div>
            <button
              className='iss-btn iss-btn-sm ms-auto'
              style={{background: '#eef2ff', color: '#4f46e5', fontWeight: 700, whiteSpace: 'nowrap'}}
              onClick={genFixPrompt}
              disabled={aiLoading}
              title='צור פרומפט מוכן ל-Claude Code לתיקון התקלה'
            >
              {aiLoading ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='bi bi-stars me-1'></i>}
              פרומפט תיקון AI
            </button>
            <button className='iss-iconbtn danger' onClick={doDelete} title='מחק תקלה'>
              <i className='bi bi-trash'></i>
            </button>
          </div>

          {/* Details */}
          <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 6}}>
            <i className='bi bi-info-circle me-1'></i>
            פרטי התקלה
            {issue.opened_by && <> · נפתח ע״י {issue.opened_by}</>}
            {' · '}
            {fullDate(issue.created_at)}
          </div>
          {issue.description ? (
            <p style={{fontSize: 14, color: '#3a4151', lineHeight: 1.6, marginBottom: 14}}>
              {issue.description}
            </p>
          ) : (
            <p style={{fontSize: 13, color: '#b3b9c7', marginBottom: 14}}>אין תיאור</p>
          )}

          {issue.attachments.length > 0 && (
            <div className='mb-4'>
              <AttachmentList attachments={issue.attachments} />
            </div>
          )}

          {/* Affected accounts */}
          <div className='d-flex align-items-center justify-content-between mb-1'>
            <span style={{fontSize: 13, fontWeight: 700, color: '#1b1f2a'}}>
              חשבונות מושפעים ({issue.accounts.length})
            </span>
            <button className='iss-btn iss-btn-ghost iss-btn-sm' onClick={() => setShowAddAccts(!showAddAccts)}>
              <i className='bi bi-plus-lg'></i>
              הוסף חשבון
            </button>
          </div>

          {showAddAccts && (
            <div style={{background: '#f8f9fb', borderRadius: 12, padding: 12, marginBottom: 10}}>
              <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 6}}>
                הוסף חשבונות נוספים שיש להם את אותה התקלה
              </div>
              <AccountPicker selected={adding} onChange={setAdding} />
              {adding.length > 0 && (
                <button className='iss-btn iss-btn-primary iss-btn-sm mt-2' onClick={doAddAccounts}>
                  הוסף {adding.length} חשבונות
                </button>
              )}
            </div>
          )}

          <div className='mb-4'>
            {issue.accounts.map((a) => (
              <div key={a.account} className='iss-acct-row'>
                <Avatar name={a.name || a.email || a.account} size={36} />
                <div style={{minWidth: 0, flex: 1}}>
                  <div className='fw-bold text-truncate' style={{fontSize: 13}}>
                    {a.name || a.account}
                  </div>
                  <div className='text-truncate' style={{fontSize: 11, color: '#8a92a6'}}>
                    {a.email}
                  </div>
                  {a.phone && (
                    <a
                      href={`tel:${a.phone}`}
                      style={{fontSize: 11, color: '#5b6ef5', textDecoration: 'none'}}
                      title='התקשר ללקוח'
                    >
                      <i className='bi bi-telephone me-1'></i>
                      {a.phone}
                    </a>
                  )}
                </div>
                <input
                  className='iss-input'
                  style={{flex: 1.4, padding: '6px 10px'}}
                  defaultValue={a.note}
                  placeholder='ממצאי בדיקה...'
                  onBlur={(e) => {
                    if (e.target.value !== a.note) setAccountNote(a.account, e.target.value)
                  }}
                />
                <StatusSeg value={a.status} onChange={(s) => setAccountStatus(a.account, s)} />
                {a.confirmed_at ? (
                  <span style={{fontSize: 10.5, color: '#16a34a', fontWeight: 700, background: '#dcfce7', borderRadius: 8, padding: '4px 8px', whiteSpace: 'nowrap'}} title={`${fullDate(a.confirmed_at)}${a.confirmed_by ? ' · ' + a.confirmed_by : ''}`}>
                    <i className='bi bi-patch-check-fill me-1'></i>אושר
                  </span>
                ) : a.status === 'done' ? (
                  <button className='iss-btn iss-btn-sm' style={{background: '#dcfce7', color: '#15803d', fontWeight: 700, whiteSpace: 'nowrap'}} onClick={() => confirmAccountFixed(a.account)} title='אשר שתוקן לחשבון זה'>
                    <i className='bi bi-patch-check me-1'></i>אשר
                  </button>
                ) : null}
                <button
                  className='iss-btn iss-btn-ghost iss-btn-sm'
                  onClick={() => loginAsAccount(a.account)}
                  title='התחבר לחשבון זה'
                >
                  <i className='bi bi-box-arrow-in-left'></i>
                  התחבר
                </button>
                <button className='iss-iconbtn danger' onClick={() => doRemoveAccount(a.account)}>
                  <i className='bi bi-x-lg'></i>
                </button>
              </div>
            ))}
            {issue.accounts.length === 0 && (
              <div style={{fontSize: 13, color: '#b3b9c7', padding: '10px 0'}}>אין חשבונות מקושרים</div>
            )}
          </div>

          {/* Comments / follow-up */}
          <div style={{background: '#f8f9fb', borderRadius: 14, padding: 16}}>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <span style={{fontSize: 13, fontWeight: 700, color: '#1b1f2a'}}>
                <i className='bi bi-chat-dots me-1'></i>
                תגובות ושאלות המשך
              </span>
              {issue.opened_by && (
                <span style={{fontSize: 11, color: '#8a92a6'}}>
                  שאל את {issue.opened_by} (פותח התקלה)
                </span>
              )}
            </div>

            <div className='mb-3'>
              {issue.replies.map((r, i) => {
                const author = r.by_admin || (r.by_role === 'admin' ? 'אדמין' : 'לקוח')
                const mine = !!r.by_admin_id && r.by_admin_id === (admin as any)?._id
                return (
                  <div key={r._id || i} className='d-flex gap-2 mb-3'>
                    <Avatar name={author} size={30} />
                    <div style={{flex: 1, minWidth: 0}}>
                      <div
                        className='d-flex align-items-center justify-content-between'
                        style={{fontSize: 11, color: '#8a92a6', marginBottom: 3}}
                      >
                        <span>
                          <span className='fw-bold' style={{color: '#4a5163'}}>
                            {author}
                          </span>{' '}
                          · {r.date ? timeAgo(r.date) : ''}
                        </span>
                        {mine && (
                          <i
                            className='bi bi-trash'
                            style={{cursor: 'pointer', color: '#c0c5d0'}}
                            title='מחק תגובה'
                            onClick={() => doDeleteReply(r._id)}
                          ></i>
                        )}
                      </div>
                      {r.text && (
                        <div className='iss-bubble'>
                          <Linkify text={r.text} />
                        </div>
                      )}
                      {r.attachments && r.attachments.length > 0 && (
                        <div className='mt-2'>
                          <AttachmentList attachments={r.attachments} size={84} />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              {issue.replies.length === 0 && (
                <div style={{fontSize: 12, color: '#b3b9c7', textAlign: 'center', padding: '8px 0'}}>
                  אין תגובות עדיין — שאל שאלה כדי לקבל משוב
                </div>
              )}
            </div>

            <div>
              <div className='d-flex gap-2 align-items-center mb-2'>
                <input
                  className='iss-input'
                  placeholder='כתוב תגובה, הדבק קישור...'
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') doReply()
                  }}
                />
                <button
                  className='iss-btn iss-btn-primary'
                  onClick={doReply}
                  disabled={replyUploading || (!reply.trim() && !replyFiles.length)}
                >
                  <i className='bi bi-send'></i>
                  שלח
                </button>
              </div>
              {/* Always-visible drop section for media (drag or click) */}
              <MediaDropzone
                attachments={replyFiles}
                uploading={replyUploading}
                onPick={onPickReplyFiles}
                onRemove={(idx) => setReplyFiles((p) => p.filter((_, k) => k !== idx))}
              />
            </div>
          </div>
        </div>
      )}

      {/* AI fix-prompt modal */}
      {aiOpen && (
        <div
          onClick={() => setAiOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,18,28,0.45)', zIndex: 1080,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            dir='rtl'
            style={{
              background: '#fff', borderRadius: 14, width: 'min(720px, 96vw)', maxHeight: '88vh',
              display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div className='d-flex align-items-center justify-content-between' style={{padding: '16px 20px', borderBottom: '1px solid #f1f2f5'}}>
              <h3 style={{margin: 0, fontSize: 16, fontWeight: 700, color: '#1b1f2a'}}>
                <i className='bi bi-stars me-2' style={{color: '#4f46e5'}}></i>
                פרומפט תיקון ל-Claude Code
              </h3>
              <i className='bi bi-x-lg' style={{cursor: 'pointer', color: '#b3b9c7'}} onClick={() => setAiOpen(false)}></i>
            </div>
            <div style={{padding: 20, overflowY: 'auto'}}>
              {aiLoading ? (
                <div style={{textAlign: 'center', color: '#8a92a6', padding: '24px 0'}}>
                  <span className='spinner-border spinner-border-sm me-2'></span>
                  מנתח את התקלה ובונה פרומפט...
                </div>
              ) : !aiResult ? null : !aiResult.relevant ? (
                <div style={{fontSize: 14, color: '#3a4151', lineHeight: 1.7}}>
                  <div style={{fontWeight: 700, color: '#b45309', marginBottom: 6}}>
                    <i className='bi bi-exclamation-triangle me-1'></i>
                    התקלה אינה מתאימה לתיקון אוטומטי בקוד
                  </div>
                  {aiResult.reason || 'לא ניתן לייצר פרומפט תיקון מהתקלה הזו.'}
                </div>
              ) : (
                <>
                  {aiResult.reason && (
                    <div style={{fontSize: 12, color: '#8a92a6', marginBottom: 10}}>{aiResult.reason}</div>
                  )}
                  <textarea
                    readOnly
                    value={aiResult.prompt}
                    dir='ltr'
                    style={{
                      width: '100%', minHeight: 280, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5,
                      color: '#1b1f2a', background: '#f8f9fb', border: '1px solid #e6e8ee', borderRadius: 10,
                      padding: 12, resize: 'vertical',
                    }}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                </>
              )}
            </div>
            {aiResult?.relevant && !aiLoading && (
              <div className='d-flex justify-content-end gap-2' style={{padding: '14px 20px', borderTop: '1px solid #f1f2f5'}}>
                <button
                  className='iss-btn iss-btn-primary'
                  onClick={copyAiPrompt}
                  style={{background: aiCopied ? '#16a34a' : undefined, borderColor: aiCopied ? '#16a34a' : undefined}}
                >
                  <i className={`bi ${aiCopied ? 'bi-check2' : 'bi-clipboard'} me-1`}></i>
                  {aiCopied ? 'הועתק!' : 'העתק פרומפט'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const IssuesPage: FC = () => {
  const {admin} = useAdminAuth()
  const [issues, setIssues] = useState<AccountIssue[]>([])
  const [openCount, setOpenCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('open')
  const [search, setSearch] = useState('')
  const [myQueue, setMyQueue] = useState(false)
  const [waitingFilter, setWaitingFilter] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState('') // filter by user (admin id)
  const [departmentFilter, setDepartmentFilter] = useState('') // filter by department
  const [filtersLoaded, setFiltersLoaded] = useState(false)
  const [admins, setAdmins] = useState<AdminOption[]>([])
  const [view, setView] = useState<'list' | 'calendar'>('list')

  // Persist filter selections per admin user (each admin keeps their own view).
  const filtersKey = admin?._id ? `issues_filters_${admin._id}` : ''
  useEffect(() => {
    if (!filtersKey) return
    try {
      const raw = localStorage.getItem(filtersKey)
      if (raw) {
        const f = JSON.parse(raw)
        if (typeof f.statusFilter === 'string') setStatusFilter(f.statusFilter)
        if (typeof f.waitingFilter === 'string') setWaitingFilter(f.waitingFilter)
        if (typeof f.assigneeFilter === 'string') setAssigneeFilter(f.assigneeFilter)
        if (typeof f.departmentFilter === 'string') setDepartmentFilter(f.departmentFilter)
        if (typeof f.myQueue === 'boolean') setMyQueue(f.myQueue)
        if (f.view === 'list' || f.view === 'calendar') setView(f.view)
      }
    } catch {}
    setFiltersLoaded(true)
  }, [filtersKey])

  useEffect(() => {
    if (!filtersKey || !filtersLoaded) return
    try {
      localStorage.setItem(filtersKey, JSON.stringify({statusFilter, waitingFilter, assigneeFilter, departmentFilter, myQueue, view}))
    } catch {}
  }, [filtersKey, filtersLoaded, statusFilter, waitingFilter, assigneeFilter, departmentFilter, myQueue, view])

  // New issue form
  const [showNew, setShowNew] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [newDepartment, setNewDepartment] = useState('')
  const [newAssignee, setNewAssignee] = useState('')
  const [newAccounts, setNewAccounts] = useState<AccountSearchResult[]>([])
  const [newAttachments, setNewAttachments] = useState<IssueAttachment[]>([])
  const [uploading, setUploading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getIssues({
        status: statusFilter || undefined,
        q: search.trim() || undefined,
        waiting: waitingFilter || undefined,
        department: departmentFilter || undefined,
        assigned_to: assigneeFilter || (myQueue && admin?._id ? admin._id : undefined),
      })
      setIssues(res.data.issues || [])
      setOpenCount(res.data.open_count || 0)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, search, waitingFilter, departmentFilter, assigneeFilter, myQueue, admin])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    getIssueAdmins()
      .then((res) => setAdmins(res.data.admins || []))
      .catch(() => setAdmins([]))
  }, [])

  const onChanged = (updated: AccountIssue) =>
    setIssues((prev) => prev.map((i) => (i._id === updated._id ? updated : i)))
  const onDeleted = (id: string) => setIssues((prev) => prev.filter((i) => i._id !== id))

  const onPickFiles = async (files: FileList | null) => {
    if (!files || !files.length) return
    setUploading(true)
    try {
      const uploaded: IssueAttachment[] = []
      for (let i = 0; i < files.length; i++) {
        const res = await uploadIssueMedia(files[i])
        uploaded.push(res.data)
      }
      setNewAttachments((prev) => [...prev, ...uploaded])
    } finally {
      setUploading(false)
    }
  }

  const doCreate = async () => {
    if (!newTitle.trim()) return
    await createIssue({
      title: newTitle.trim(),
      description: newDesc.trim(),
      department: newDepartment || undefined,
      assigned_to: newAssignee || undefined,
      account_ids: newAccounts.map((a) => a._id),
      attachments: newAttachments,
    })
    setNewTitle('')
    setNewDesc('')
    setNewDepartment('')
    setNewAssignee('')
    setNewAccounts([])
    setNewAttachments([])
    setShowNew(false)
    load()
  }

  const filterChip = (label: string, active: boolean, onClick: () => void) => (
    <button className={`iss-btn iss-btn-ghost iss-btn-sm ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </button>
  )

  return (
    <div
      className='iss-scope'
      dir='rtl'
      style={{maxWidth: view === 'calendar' ? 1440 : 1120, margin: '0 auto', padding: '32px 22px'}}
    >
      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 15,
              background: 'linear-gradient(135deg, #ff6a88 0%, #f1416c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(241, 65, 108, 0.32)',
            }}
          >
            <i className='bi bi-exclamation-triangle-fill' style={{color: '#fff', fontSize: 20}}></i>
          </div>
          <div>
            <h1 style={{fontSize: 24, fontWeight: 800, margin: 0, color: '#14161f', letterSpacing: '-0.4px'}}>תקלות</h1>
            <div style={{fontSize: 12.5, color: '#8b93a7', fontWeight: 500}}>{openCount} תקלות פתוחות</div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <div className='iss-seg'>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
              <i className='bi bi-list-ul me-1'></i>רשימה
            </button>
            <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>
              <i className='bi bi-calendar3 me-1'></i>יומן
            </button>
          </div>
          {view === 'list' && (
            <button className='iss-btn iss-btn-primary' onClick={() => setShowNew(!showNew)}>
              <i className={`bi bi-${showNew ? 'x-lg' : 'plus-lg'}`}></i>
              {showNew ? 'ביטול' : 'תקלה חדשה'}
            </button>
          )}
        </div>
      </div>

      {view === 'calendar' && <IssuesCalendar admins={admins} />}

      {/* New issue */}
      {view === 'list' && showNew && (
        <div className='iss-card mb-4' style={{padding: 20}}>
          <input
            className='iss-input mb-2'
            placeholder='כותרת התקלה'
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <textarea
            className='iss-textarea mb-2'
            placeholder='תיאור מפורט (אופציונלי)'
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
          <div className='d-flex gap-2 mb-2' style={{flexWrap: 'wrap'}}>
            <div style={{flex: 1, minWidth: 160}}>
              <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 4}}>מחלקה</div>
              <select
                className='iss-input'
                value={newDepartment}
                onChange={(e) => {
                  setNewDepartment(e.target.value)
                  setNewAssignee('') // reset assignee when department changes
                }}
              >
                <option value=''>ללא מחלקה</option>
                {ISSUE_DEPARTMENTS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{flex: 1, minWidth: 160}}>
              <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 4}}>שיוך לנציג</div>
              <select className='iss-input' value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
                <option value=''>לא משויך</option>
                {(newDepartment ? admins.filter((a) => a.departments?.includes(newDepartment)) : admins).map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.username}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className='mb-2'>
            <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 4}}>חשבונות מושפעים (אופציונלי)</div>
            <AccountPicker selected={newAccounts} onChange={setNewAccounts} />
          </div>
          <div className='mb-3'>
            <div style={{fontSize: 11, color: '#8a92a6', marginBottom: 4}}>מדיה (אופציונלי)</div>
            <MediaDropzone
              attachments={newAttachments}
              uploading={uploading}
              onPick={onPickFiles}
              onRemove={(idx) => setNewAttachments((p) => p.filter((_, k) => k !== idx))}
            />
          </div>
          <div className='d-flex align-items-center gap-2'>
            <button className='iss-btn iss-btn-primary ms-auto' onClick={doCreate} disabled={uploading || !newTitle.trim()}>
              צור תקלה
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {view === 'list' && (<>
      <div className='d-flex flex-wrap align-items-center gap-2 mb-4'>
        <div className='iss-seg'>
          <button className={statusFilter === '' ? 'active' : ''} onClick={() => setStatusFilter('')}>
            הכל
          </button>
          {STATUS_ORDER.map((s) => (
            <button key={s} className={statusFilter === s ? 'active' : ''} onClick={() => setStatusFilter(s)}>
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
        {filterChip('ממתין לאריה', waitingFilter === 'aryeh', () =>
          setWaitingFilter(waitingFilter === 'aryeh' ? '' : 'aryeh')
        )}
        {filterChip('ממתין לתמיכה', waitingFilter === 'support', () =>
          setWaitingFilter(waitingFilter === 'support' ? '' : 'support')
        )}
        <button
          className={`iss-btn iss-btn-ghost iss-btn-sm ${myQueue ? 'active' : ''}`}
          onClick={() => {
            setMyQueue(!myQueue)
            if (!myQueue) setAssigneeFilter('') // "my issues" and per-user filter are mutually exclusive
          }}
          disabled={!admin}
        >
          <i className='bi bi-person-check'></i>
          התקלות שלי
        </button>
        <select
          className='iss-input'
          style={{width: 150}}
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          title='סינון לפי מחלקה'
        >
          <option value=''>כל המחלקות</option>
          {ISSUE_DEPARTMENTS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <select
          className='iss-input'
          style={{width: 150}}
          value={assigneeFilter}
          onChange={(e) => {
            setAssigneeFilter(e.target.value)
            if (e.target.value) setMyQueue(false)
          }}
          title='סינון לפי נציג'
        >
          <option value=''>כל הנציגים</option>
          {(departmentFilter ? admins.filter((a) => a.departments?.includes(departmentFilter)) : admins).map((a) => (
            <option key={a._id} value={a._id}>
              {a.username}
            </option>
          ))}
        </select>
        <input
          className='iss-input ms-auto'
          style={{width: 220}}
          placeholder='חיפוש לפי כותרת...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className='iss-empty'>
          <span className='spinner-border text-primary' role='status'></span>
        </div>
      ) : issues.length === 0 ? (
        <div className='iss-empty'>
          <i className='bi bi-inbox' style={{fontSize: 40, opacity: 0.4}}></i>
          <div className='mt-2'>אין תקלות להצגה</div>
        </div>
      ) : (
        issues.map((issue) => (
          <IssueCard key={issue._id} issue={issue} admins={admins} onChanged={onChanged} onDeleted={onDeleted} />
        ))
      )}
      </>)}
    </div>
  )
}

export default IssuesPage
